import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Bell, Check, CheckCheck, Trash2, Loader2, X, Mail, Users, Share2, CreditCard, Megaphone, UserPlus, UserMinus, Shield, ListTodo, FolderKanban, Copy, CopyCheck, type LucideIcon } from 'lucide-react';
import { useNotifications } from '../hooks/useNotifications';
import { AppNotification, NotificationType } from '../types';

// ── Helpers ──────────────────────────────────────────────────────────────────

const ICON_MAP: Partial<Record<NotificationType, LucideIcon>> = {
  invitation_received: Mail,
  invitation_accepted: UserPlus,
  workspace_shared: Share2,
  role_changed: Shield,
  member_joined: UserPlus,
  member_removed: UserMinus,
  team_added: Users,
  team_removed: Users,
  subscription_expiring: CreditCard,
  subscription_changed: CreditCard,
  system_announcement: Megaphone,
  task_assigned: ListTodo,
  task_done: ListTodo,
  project_invitation: FolderKanban,
  project_copy: Copy,
  project_copy_done: CopyCheck,
};

/** How an invitation was answered, as its notification says it */
const ANSWERED: Record<string, string> = { accepted: 'Accepted', declined: 'Declined', cancelled: 'No longer open' };

/** An address of this app ("/..."), the only kind of link followed: a notification is a record anyone signed in may be able to write, and a link out of the app, or a script, has no business in it */
const appAddress = (url: unknown): string | null => (typeof url === 'string' && /^\/(?!\/)/.test(url) ? url : null);

/**
 * Where a notification leads. Its link, except that a notice about a project whose link is
 * the bare library (as the ones written before the library could be sent to a project) leads
 * to the project itself: the owner's own, or under Shared with me once accepted, and to
 * Shared with me, where an invitation waits, until then.
 */
export const linkOf = (n: AppNotification, answered?: string): string | null => {
  const url = appAddress(n.actionUrl);
  if (url && url !== '/workspaces') return url;
  const folderId = typeof n.data?.folderId === 'string' ? n.data.folderId : null;
  if (n.type === 'invitation_accepted' && folderId) return `/workspaces?project=${encodeURIComponent(folderId)}`;
  if (n.type === 'project_invitation') {
    const said = answered ?? n.data?.answer;
    return said === 'accepted' && folderId ? `/workspaces?view=shared&project=${encodeURIComponent(folderId)}` : '/workspaces?view=shared';
  }
  return url;
};

function timeAgo(ts: number): string {
  const seconds = Math.floor((Date.now() - ts) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(ts).toLocaleDateString();
}

// ── Component ────────────────────────────────────────────────────────────────

interface NotificationBellProps {
  userId: string | undefined;
  className?: string;
  /** Open a notification's link inside the app; true when it did (otherwise the address is loaded). */
  onOpenLink?: (url: string) => boolean;
  /** Answers an invitation to a project; resolves true when the answer went through */
  onAnswerInvitation?: (invitationId: string, accept: boolean) => Promise<boolean>;
  /** Notifications that arrived while the page was open, so what they are about can be shown afresh */
  onArrive?: (notifications: AppNotification[]) => void;
}

export function NotificationBell({ userId, className = '', onOpenLink, onAnswerInvitation, onArrive }: NotificationBellProps) {
  const {
    notifications,
    unreadCount,
    isLoading,
    error,
    hasMore,
    refresh,
    loadMore,
    markRead,
    markAllRead,
    deleteNotification,
  } = useNotifications(userId, { onArrive });

  const [isOpen, setIsOpen] = useState(false);
  /** Invitations answered here: the answer shows at once, before the list is read again */
  const [answers, setAnswers] = useState<Record<string, 'accepted' | 'declined' | 'busy'>>({});
  const panelRef = useRef<HTMLDivElement>(null);
  const bellRef = useRef<HTMLButtonElement>(null);

  // Close on a press outside. (The press itself, not the mouse event that follows it: the canvas
  // keeps its own presses from becoming mouse events, so a press on it never got here.)
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: PointerEvent) => {
      if (
        panelRef.current && !panelRef.current.contains(e.target as Node) &&
        bellRef.current && !bellRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('pointerdown', handler);
    return () => document.removeEventListener('pointerdown', handler);
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen]);

  const handleToggle = useCallback(() => {
    setIsOpen(prev => {
      if (!prev) refresh(); // Refresh on open
      return !prev;
    });
  }, [refresh]);

  const handleNotificationClick = useCallback((n: AppNotification) => {
    if (!n.read) markRead(n.id);
    const given = answers[n.id];
    const url = linkOf(n, given && given !== 'busy' ? given : undefined);
    if (!url) return;
    // A link the app opens itself (a workspace, a project, a page) closes the panel; any other loads its address
    if (onOpenLink?.(url)) { setIsOpen(false); return; }
    window.location.href = url;
  }, [markRead, onOpenLink, answers]);

  const answer = useCallback(async (n: AppNotification, accept: boolean) => {
    const invitationId = n.data?.invitationId;
    if (!onAnswerInvitation || typeof invitationId !== 'string') return;
    setAnswers(prev => ({ ...prev, [n.id]: 'busy' }));
    const done = await onAnswerInvitation(invitationId, accept).catch(() => false);
    setAnswers(prev => {
      const next = { ...prev };
      if (done) next[n.id] = accept ? 'accepted' : 'declined';
      else delete next[n.id];
      return next;
    });
    if (done) refresh();
  }, [onAnswerInvitation, refresh]);

  if (!userId) return null;

  return (
    <div className={`relative flex ${className}`}>
      {/* Bell trigger: one of the round buttons of the top bar, on (lavender) while its panel is open */}
      <button
        ref={bellRef}
        onClick={handleToggle}
        className="ns-icon-btn relative"
        aria-haspopup="true"
        aria-expanded={isOpen}
        aria-pressed={isOpen}
        title="Notifications"
        aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
      >
        <Bell />
        {/* The count of what is new: lavender, as everything that is new or selected (red is for errors) */}
        {unreadCount > 0 && (
          <span className="ns-badge absolute -top-1 -right-1 justify-center min-w-[18px] h-[18px] px-1 py-0 rounded-full bg-lavender-deep text-white ring-2 ring-white animate-pulse">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown panel */}
      {isOpen && (
        <div
          ref={panelRef}
          className="ns-menu p-0 absolute right-0 top-full mt-2 w-[360px] max-w-[calc(100vw-2rem)] overflow-hidden z-[200]"
        >
          {/* Header: the same as a drawer's */}
          <div className="ns-drawer-head">
            <h2 className="ns-t-heading">Notifications</h2>
            <div className="flex items-center gap-1">
              {unreadCount > 0 && (
                <button
                  onClick={markAllRead}
                  className="ns-btn ns-btn-soft ns-btn-xs ns-btn-flat"
                  title="Mark all as read"
                >
                  <CheckCheck size={14} />
                  Mark all read
                </button>
              )}
              <button
                onClick={() => setIsOpen(false)}
                className="ns-icon-btn"
                title="Close"
                aria-label="Close"
              >
                <X />
              </button>
            </div>
          </div>

          {/* Notification list */}
          <div className="max-h-[400px] overflow-y-auto">
            {error && notifications.length === 0 && !isLoading ? (
              // Could not be read: said so, with the way to try again (not "nothing yet", which hid a failing server)
              <div className="ns-empty py-10" role="alert">
                <span className="ns-mark ns-mark-lg" data-tone="danger"><Bell /></span>
                <p className="ns-empty-title">The notifications could not be read</p>
                <p className="ns-empty-text">{error}</p>
                <button type="button" className="ns-btn ns-btn-sm ns-btn-ghost ns-btn-flat mt-3" onClick={() => { void refresh(); }}>Try again</button>
              </div>
            ) : notifications.length === 0 && !isLoading ? (
              <div className="ns-empty py-10">
                <span className="ns-mark ns-mark-lg ns-mark-lavender"><Bell /></span>
                <p className="ns-empty-title">No notifications yet</p>
                <p className="ns-empty-text">We'll notify you about important updates.</p>
              </div>
            ) : (
              <>
                {notifications.map(n => {
                  const NIcon = ICON_MAP[n.type] || Bell;
                  return (
                    <div
                      key={n.id}
                      onClick={() => handleNotificationClick(n)}
                      className={`
                        flex items-start gap-3 px-4 py-3 border-b border-cream-200 cursor-pointer transition-colors
                        ${n.read ? 'hover:bg-cream-200/60' : 'bg-lavender-soft/40 hover:bg-lavender-soft/70'}
                      `}
                    >
                      {/* Type icon, in the round mark of a row: lavender while unread */}
                      <span className={`ns-mark ns-mark-sm ${n.read ? 'bg-cream-200 text-ink-faint' : 'ns-mark-lavender'}`}>
                        <NIcon />
                      </span>

                      {/* Content */}
                      <div className="flex-1 min-w-0 pt-0.5">
                        <p className={`text-[13px] leading-snug ${n.read ? 'text-ink-soft' : 'text-ink font-semibold'}`}>
                          {n.title}
                        </p>
                        {n.body && (
                          <p className="ns-t-small mt-0.5 line-clamp-2">{n.body}</p>
                        )}
                        {n.type === 'project_invitation' && typeof n.data?.invitationId === 'string' && (() => {
                          const given = answers[n.id];
                          const said = given && given !== 'busy' ? given : n.data?.answer;
                          if (said) return <p className="ns-t-small mt-1 font-semibold" data-invitation-answer={said}>{ANSWERED[said] ?? said}</p>;
                          if (!onAnswerInvitation) return null;
                          return (
                            <div className="flex items-center gap-1.5 mt-1.5" data-invitation={n.data.invitationId}>
                              <button type="button" className="ns-btn ns-btn-primary ns-btn-xs" disabled={given === 'busy'} onClick={(e) => { e.stopPropagation(); void answer(n, true); }}>
                                {given === 'busy' ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}Accept
                              </button>
                              <button type="button" className="ns-btn ns-btn-ghost ns-btn-xs" disabled={given === 'busy'} onClick={(e) => { e.stopPropagation(); void answer(n, false); }}>
                                Decline
                              </button>
                            </div>
                          );
                        })()}
                        {n.type === 'project_copy' && typeof n.data?.answer === 'string' && (
                          <p className="ns-t-small mt-1 font-semibold" data-copy-answer={n.data.answer}>{ANSWERED[n.data.answer] ?? n.data.answer}</p>
                        )}
                        <p className="ns-t-meta mt-1">{timeAgo(n.createdAt)}</p>
                      </div>

                      {/* Actions */}
                      <div className="flex-shrink-0 flex items-center gap-0.5">
                        {!n.read && (
                          <button
                            onClick={(e) => { e.stopPropagation(); markRead(n.id); }}
                            className="ns-icon-btn ns-icon-btn-xs"
                            title="Mark as read"
                            aria-label="Mark as read"
                          >
                            <Check />
                          </button>
                        )}
                        <button
                          onClick={(e) => { e.stopPropagation(); deleteNotification(n.id); }}
                          className="ns-icon-btn ns-icon-btn-xs ns-icon-btn-danger"
                          title="Delete"
                          aria-label="Delete"
                        >
                          <Trash2 />
                        </button>
                      </div>
                    </div>
                  );
                })}

                {/* Load more */}
                {hasMore && (
                  <div className="p-2">
                    <button
                      onClick={loadMore}
                      disabled={isLoading}
                      className="ns-btn ns-btn-soft ns-btn-xs ns-btn-flat w-full"
                    >
                      {isLoading ? <><Loader2 size={14} className="animate-spin" /> Loading...</> : 'Load more'}
                    </button>
                  </div>
                )}
              </>
            )}

            {isLoading && notifications.length === 0 && (
              <div className="flex items-center justify-center py-12 text-ink-faint">
                <Loader2 size={20} className="animate-spin" />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
